import type { ReactNode } from "react";
import type { AdminSection } from "../types";
import { API_BASE_URL } from "../lib/api";

interface NavItem {
  id: AdminSection;
  label: string;
  description: string;
}

const NAV_ITEMS: NavItem[] = [
  { id: "overview", label: "Overview", description: "Activity and status" },
  { id: "content", label: "Content", description: "Copy and page structure" },
  { id: "inquiries", label: "Inquiries", description: "Business requests" },
  { id: "media", label: "Media", description: "Website image library" },
  { id: "settings", label: "Settings", description: "Mail and site details" }
];

export function sectionTitle(section: AdminSection): string {
  return NAV_ITEMS.find((item) => item.id === section)?.label || "Overview";
}

export function AdminLayout({
  section,
  onNavigate,
  onLogout,
  children
}: {
  section: AdminSection;
  onNavigate: (section: AdminSection) => void;
  onLogout: () => void;
  children: ReactNode;
}) {
  return (
    <div className="admin-shell">
      <a className="skip-link" href="#main-content">
        Skip to main content
      </a>
      <aside className="sidebar">
        <div className="sidebar__heading">
          <div className="wordmark">ATALAY</div>
          <span>Administration</span>
        </div>
        <nav className="main-nav" aria-label="Administration sections">
          {NAV_ITEMS.map((item, index) => (
            <button
              type="button"
              key={item.id}
              className={section === item.id ? "is-active" : ""}
              aria-current={section === item.id ? "page" : undefined}
              onClick={() => onNavigate(item.id)}
            >
              <span className="main-nav__number">
                {String(index + 1).padStart(2, "0")}
              </span>
              <span>
                <strong>{item.label}</strong>
                <small>{item.description}</small>
              </span>
            </button>
          ))}
        </nav>
        <div className="sidebar__footer">
          <span className="connection-dot" aria-hidden="true" />
          <span>Connected to {new URL(API_BASE_URL).hostname}</span>
          <button type="button" className="text-button" onClick={onLogout}>
            Clear key &amp; lock
          </button>
        </div>
      </aside>

      <div className="admin-main">
        <header className="mobile-header">
          <div>
            <div className="wordmark">ATALAY</div>
            <span>Administration</span>
          </div>
          <button type="button" className="text-button" onClick={onLogout}>
            Lock
          </button>
        </header>
        <nav className="mobile-nav" aria-label="Administration sections">
          {NAV_ITEMS.map((item) => (
            <button
              type="button"
              key={item.id}
              className={section === item.id ? "is-active" : ""}
              aria-current={section === item.id ? "page" : undefined}
              onClick={() => onNavigate(item.id)}
            >
              {item.label}
            </button>
          ))}
        </nav>
        <main id="main-content" className="content-area" tabIndex={-1}>
          {children}
        </main>
      </div>
    </div>
  );
}
